import { getBenefitDisplayLabel } from "@/lib/benefit-labels"
import {
  formatGroupOptionalBenefitDisplay,
  resolveGroupOptionalBenefitRow,
  type OptionalBenefitCatalogGroup,
} from "@/lib/resolve-group-optional-benefit"

type CategoryGroupPlanTier = {
  name?: string
  annualLimit?: string
  roomType?: string
  planTierLabel?: string | null
  usaCanadaIncluded?: boolean | null
  staticDetails?: Record<string, unknown> | null
}

export type CategoryPlanMetaItem = {
  key: string
  label: string
  value: string
}

const STATIC_DETAIL_SKIP_KEYS = new Set([
  "termsPages",
  "termsAndConditionsPages",
  "terms",
  "exclusionPages",
  "exclusions",
  "designedFor",
  "differenceCards",
  "promiseCards",
  "benefitSections",
  "keyInfo",
  "intro",
])

const STATIC_DETAIL_LABELS: Record<string, string> = {
  territory: "Territory",
}

function formatStaticDetailValue(raw: unknown): string | null {
  if (raw == null) return null
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw)
  if (typeof raw === "object" && raw !== null && "optionLabel" in raw) {
    const label = (raw as { optionLabel?: string }).optionLabel
    if (label) return label.replace(/%/g, "")
  }
  if (typeof raw === "object" && raw !== null && "value" in raw) {
    return formatStaticDetailValue((raw as { value?: unknown }).value)
  }
  return null
}

function formatFieldKeyAsLabel(key: string): string {
  return getBenefitDisplayLabel(key.replace(/([A-Z])/g, "_$1").toLowerCase())
}

function pushUnique(
  items: CategoryPlanMetaItem[],
  item: CategoryPlanMetaItem
) {
  if (items.some((existing) => existing.key === item.key)) return
  if (!item.value || item.value === "-") return
  items.push(item)
}

function catalogMatchesBenefitKey(
  catalogBenefitType: string,
  benefitKey: string
): boolean {
  const normalize = (key: string) =>
    key
      .replace(/([A-Z])/g, "_$1")
      .toLowerCase()
      .replace(/^_/, "")
  return (
    catalogBenefitType === benefitKey ||
    normalize(catalogBenefitType) === normalize(benefitKey)
  )
}

export function resolveSelectedAnnualMedicalLimit(input: {
  planTier?: { annualLimit?: string } | null
  benefitSelections?: {
    benefitSelections?: Record<string, unknown>
    optionalBenefits?: Record<string, unknown>
  } | null
  adjustmentFactors?: Array<{
    factorType: string
    selectedId: string | null
    options: Array<{ id: string; optionLabel: string; isDefault?: boolean }>
  }>
  optionalBenefitCatalog?: OptionalBenefitCatalogGroup[]
  currency?: string
}): string {
  const amlFromSelections = input.benefitSelections?.benefitSelections?.aml
  const fromAdjustmentSelections = formatStaticDetailValue(amlFromSelections)
  if (fromAdjustmentSelections) return fromAdjustmentSelections

  const amlGroup = input.optionalBenefitCatalog?.find((entry) =>
    catalogMatchesBenefitKey(entry.benefitType, "aml")
  )
  const amlRow = resolveGroupOptionalBenefitRow(
    input.benefitSelections?.optionalBenefits?.aml ??
      amlGroup?.selectedId ??
      null,
    input.optionalBenefitCatalog,
    "aml"
  )
  const amlFromOptional = formatGroupOptionalBenefitDisplay(
    amlRow,
    input.currency ?? "AED"
  )
  if (amlFromOptional !== "Not covered") return amlFromOptional

  const amlFactor = input.adjustmentFactors?.find(
    (factor) => factor.factorType === "aml"
  )
  if (amlFactor) {
    const selected =
      amlFactor.options.find((option) => option.id === amlFactor.selectedId) ??
      amlFactor.options.find((option) => option.isDefault) ??
      amlFactor.options[0]
    if (selected?.optionLabel) return selected.optionLabel.replace(/%/g, "")
  }

  return input.planTier?.annualLimit ?? ""
}

/**
 * Plan-tier fields for a merged quote category (annual limit, room type,
 * staticDetails scalars, etc.). Network / TPA cells are added separately in
 * the page because they need quote-level resolution.
 */
export function buildCategoryPlanMetaDetails(input: {
  planTier?: CategoryGroupPlanTier | null
  product?: { emirate?: string | null; type?: string | null } | null
  benefitSelections?: {
    benefitSelections?: Record<string, unknown>
    optionalBenefits?: Record<string, unknown>
  } | null
  adjustmentFactors?: Array<{
    factorType: string
    selectedId: string | null
    options: Array<{ id: string; optionLabel: string; isDefault?: boolean }>
  }>
  optionalBenefitCatalog?: OptionalBenefitCatalogGroup[]
  currency?: string
}): CategoryPlanMetaItem[] {
  const tier = input.planTier
  if (!tier) return []

  const items: CategoryPlanMetaItem[] = []

  pushUnique(items, {
    key: "annualLimit",
    label: "Annual Medical Limit",
    value: resolveSelectedAnnualMedicalLimit({
      planTier: tier,
      benefitSelections: input.benefitSelections,
      adjustmentFactors: input.adjustmentFactors,
      optionalBenefitCatalog: input.optionalBenefitCatalog,
      currency: input.currency,
    }),
  })

  if (tier.planTierLabel && tier.planTierLabel !== tier.name) {
    pushUnique(items, {
      key: "planTierLabel",
      label: "Plan Label",
      value: tier.planTierLabel,
    })
  }

  if (tier.usaCanadaIncluded != null) {
    pushUnique(items, {
      key: "usaCanadaIncluded",
      label: "USA & Canada Cover",
      value: tier.usaCanadaIncluded ? "Included" : "Excluded",
    })
  }

  const staticDetails = tier.staticDetails ?? {}
  let hasTerritory = false
  for (const [key, raw] of Object.entries(staticDetails)) {
    if (STATIC_DETAIL_SKIP_KEYS.has(key)) continue
    const value = formatStaticDetailValue(raw)
    if (!value) continue
    if (key === "territory") hasTerritory = true
    pushUnique(items, {
      key: `static:${key}`,
      label: STATIC_DETAIL_LABELS[key] ?? formatFieldKeyAsLabel(key),
      value,
    })
  }

  if (
    !hasTerritory &&
    input.product?.emirate &&
    input.product.emirate !== "all"
  ) {
    pushUnique(items, {
      key: "coverageArea",
      label: "Coverage Area",
      value: input.product.emirate.toUpperCase().replace(/_/g, " "),
    })
  }

  return items
}
